import { useRef, useState } from 'react';
import PageTabs from '@/components/page/PageTabs';
import PaginationButtons from '@/components/PaginationButtons';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { query } from '@/redux/selectors/querySelectors';
import ConfigurationSection from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/outputPanel/outputViewer/ConfigurationSection';
import HeaderSection from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/outputPanel/outputViewer/HeaderSection';
import ResponseSection from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/outputPanel/outputViewer/ResponseSection';
import PayloadSection from './outputLogViewer/PayloadSection';
import SummarySection from './outputLogViewer/SummarySection';
import useResponsive from '@/hooks/useResponsive.js';
import getLogStepOutputPage from '../lib/getLogStepOutputPage';
import { logStepOutputPageSize } from '../lib/logStepOutputPageSize';

// This component shows up in the output panel of the logs. Runs recorded with "Capture Full Run
// Logs" keep every response row in the log head, so the Summary and Response tabs render one page
// of rows at a time. The parent keys this component by log, step, time and search, which resets
// the page when the rows behind it change. The sections are keyed by page because `OutputObjects`
// keys rows by position and would otherwise carry expand state onto the next page's rows.

const pagedTabs = ['1', '5'];

const OutputLogViewer = ({ output, search }) => {
  const [page, setPage] = useState(0);
  const contentRef = useRef(null);
  // This means an Axios request was made.
  const isAxiosRequest = output?.meta?.response && output?.meta?.response?.request;
  const configurationSectionResult = ConfigurationSection({ request: output?.meta?.request, dependencies: output?.meta?.dependencies });
  const tabKey = 'outputLogViewerTab';
  const defaultTab = '1';
  const activeTab = useArgSelector(query, tabKey) || defaultTab;
  const { tablet } = useResponsive();
  const { pageRows, totalPages, isLastPage } = getLogStepOutputPage({ rows: output?.data, page, pageSize: logStepOutputPageSize });
  const showPagination = totalPages > 1 && pagedTabs.includes(activeTab);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // The pager sits below the rows, so start the new page at its first row.
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  const childrenMap = {
    1: <SummarySection key={page} response={output} rows={pageRows} isLastPage={isLastPage} search={search} />
  };

  const items = [{ value: '1', label: 'Summary' }];

  if (output?.meta?.request?.arguments || output?.meta?.dependencies) {
    if (configurationSectionResult !== null) {
      childrenMap['2'] = configurationSectionResult;
      items.push({ value: '2', label: tablet ? 'Config' : 'Configuration' });
    }
  }

  if (isAxiosRequest) {
    childrenMap['3'] = <HeaderSection response={output?.meta?.response} />;
    items.push({ value: '3', label: 'Headers' });
  }

  if (output?.meta?.request && output?.meta?.request?.method?.toLowerCase() !== 'get') {
    childrenMap['4'] = <PayloadSection request={output?.meta?.request} />;
    items.push({ value: '4', label: 'Payload' });
  }

  if (output?.data) {
    childrenMap['5'] = <ResponseSection key={page} data={pageRows} />;
    items.push({ value: '5', label: 'Response' });
  }

  return (
    <div className="comp-output-viewer log">
      <PageTabs items={items} tabKey={tabKey} defaultTab={defaultTab} />
      <div className="content" ref={contentRef}>
        {activeTab !== undefined && childrenMap[activeTab]}
        {showPagination && <PaginationButtons total={output.data.length} limit={logStepOutputPageSize} initialPage={page} onPageChange={handlePageChange} />}
      </div>
    </div>
  );
};

export default OutputLogViewer;
